import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Instance } from '../types';
import './MonitoringPage.css';

export const MonitoringPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (instanceId) {
      fetchInstance(instanceId);
    }
  }, [instanceId]);

  const fetchInstance = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getInstanceById(id);
      setInstance(data);
    } catch (err) {
      setError('Failed to load instance details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="monitoring-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading monitoring...</p>
        </div>
      </div>
    );
  }

  if (error || !instance) {
    return (
      <div className="monitoring-page">
        <div className="error">
          <p>{error || 'Instance not found'}</p>
          <Link to="/instances" className="back-button">
            Back to Instances
          </Link>
        </div>
      </div>
    );
  }

  // To filter by instance, add a template variable named "instanceId" in the Datadog dashboard,
  // then uncomment the query parameter below:
  const embedUrl = `https://p.us5.datadoghq.com/sb/94676a39-a2eb-11ef-9cca-7e0a1d207537-4aa9e8a1c76b434b16eb6367bc3a0296?tpl_var_instanceId=${instanceId}`;
  // const embedUrl = 'https://p.us5.datadoghq.com/sb/embed/94676a39-a2eb-11ef-9cca-7e0a1d207537-4aa9e8a1c76b434b16eb6367bc3a0296';
  // const embedUrl = 'https://p.us5.datadoghq.com/sb/94676a39-a2eb-11ef-9cca-7e0a1d207537-4aa9e8a1c76b434b16eb6367bc3a0296?tpl_var_instanceId=QyHYsTXXwT1634405151';
  // const embedUrl = 'https://p.us5.datadoghq.com/sb/94676a39-a2eb-11ef-9cca-7e0a1d207537-4aa9e8a1c76b434b16eb6367bc3a0296?tpl_var_instanceId=snttbcCqyn1640807734';

  return (
    <div className="monitoring-page">
      <div className="page-header">
        <Link to={`/instances/${instance.instance}`} className="back-link">
          ← Back to Instance
        </Link>
        <div className="header-content">
          <h1>Monitoring — {instance.name}</h1>
          <span className={`status-badge ${instance.validated ? 'validated' : 'not-validated'}`}>
            {instance.validated ? 'Validated' : 'Not Validated'}
          </span>
        </div>
      </div>

      <div className="monitoring-container">
        {embedUrl ? (
          <div className="monitoring-embed">
            <iframe
              src={embedUrl}
              title={`Monitoring for ${instance.name}`}
              className="monitoring-iframe"
              width="800"
              frameBorder="0"
            />
          </div>
        ) : (
          <div className="monitoring-placeholder">
            <div className="placeholder-icon">📊</div>
            <h3>Monitoring Dashboard</h3>
            <p>
              A Datadog monitoring dashboard for this instance will be displayed here
              once configured.
            </p>
            <span className="placeholder-instance">Instance: {instance.instance}</span>
          </div>
        )}
      </div>
    </div>
  );
};
